import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Printer, Receipt as ReceiptIcon, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import { useCan, usePos } from "@/app/context";
import { MoneyText, useYen } from "@/components/MoneyText";
import { ReceiptQr } from "@/features/receipt/ReceiptQr";
import { useCart } from "@/features/sale/hooks/useCart";
import { RecordStatus } from "@/features/taxfree/RecordStatus";
import { fetchReceipt, printReceipt, qrContentOf } from "@/lib/receipts";

import type { DoneState } from "./TenderScreen";

type PrintState = "idle" | "printing" | "printed" | "failed" | "off";
type DrawerState = "idle" | "opened" | "failed";

/** Sales whose drawer / print already ran in this tab (React StrictMode runs effects twice). */
const started = new Set<string>();

/**
 * 完了 (T07-24/25): the change in big numbers, the drawer opens (cash), the receipt prints by
 * itself — a printer problem never undoes the sale: it shows 「印刷失敗」 and the job waits in the
 * print queue with 「再印刷」. 「次のお客様」 starts a new basket.
 */
export function DoneScreen() {
  const location = useLocation();
  const state = location.state as DoneState | null;
  if (!state?.sale) {
    return <Navigate to="/" replace />;
  }
  return <Done state={state} />;
}

function Done({ state }: { state: DoneState }) {
  const { t } = useTranslation();
  const yen = useYen();
  const navigate = useNavigate();
  const { api, db, printer } = usePos();
  const cart = useCart();
  const managerView = useCan("pos.reports");
  const { sale } = state;
  const saleId = sale.sale.id;
  const [print, setPrint] = useState<PrintState>(sale.receipt.print ? "printing" : "off");
  const [drawer, setDrawer] = useState<DrawerState>("idle");
  const once = useRef(false);

  useEffect(() => {
    if (once.current || started.has(saleId)) {
      return;
    }
    once.current = true;
    started.add(saleId);
    void (async () => {
      if (sale.openDrawer && printer.config.drawer) {
        const kicked = await printer.queue.openDrawer();
        setDrawer(kicked.ok ? "opened" : "failed");
      }
      if (sale.receipt.print) {
        const outcome = await printReceipt({
          api,
          db,
          printer,
          saleId,
          label: sale.sale.receiptNo,
          ...(state.locale ? { locale: state.locale } : {}),
        });
        setPrint(outcome.job?.status === "done" ? "printed" : "failed");
      }
    })();
  }, [api, db, printer, sale, saleId, state.locale]);

  const qr = useQuery({
    queryKey: ["pos-receipts", "detail", saleId, "json"],
    queryFn: () => fetchReceipt(api, saleId, { format: "json" }),
    enabled: state.qr,
    staleTime: Infinity,
  });
  const qrValue = state.qr ? qrContentOf(qr.data) : null;

  const next = async () => {
    await cart.startNew();
    void navigate("/", { replace: true });
  };

  return (
    <div className="grid h-full grid-cols-1 gap-6 p-8 lg:grid-cols-[1fr_22rem]">
      <section
        aria-labelledby="done-title"
        className="flex flex-col items-center justify-center gap-6 text-center"
      >
        <h1 id="done-title" className="flex items-center gap-3 text-3xl font-semibold text-pos-ok">
          <CheckCircle2 aria-hidden className="size-10" />
          {t("done.title")}
        </h1>
        <div>
          <p className="text-xl text-muted-foreground">
            {sale.change.amount > 0 ? t("done.change") : t("done.no_change")}
          </p>
          <MoneyText amount={sale.change.amount} size="hero" data-testid="done-change" />
        </div>
        <p className="text-lg text-muted-foreground">
          {t("done.receipt_no", { no: sale.sale.receiptNo })} ·{" "}
          {t("done.order_no", { no: sale.order.number })}
        </p>
        {managerView && sale.order.paymentFees.length > 0 ? (
          <ul data-testid="done-fees" className="text-lg">
            {sale.order.paymentFees.map((fee, i) => (
              <li key={fee.paymentId ?? i}>{t("done.fee", { amount: yen(fee.fee) })}</li>
            ))}
          </ul>
        ) : null}
        <div className="flex flex-col gap-2 text-lg" aria-live="polite">
          {drawer === "opened" ? <p>{t("done.drawer_opened")}</p> : null}
          {drawer === "failed" ? (
            <p role="alert" className="flex items-center gap-2 text-pos-problem">
              <TriangleAlert aria-hidden />
              {t("done.drawer_failed")}
            </p>
          ) : null}
          {print === "printing" ? (
            <p className="flex items-center gap-2">
              <Printer aria-hidden />
              {t("done.printing")}
            </p>
          ) : null}
          {print === "printed" ? <p>{t("done.printed")}</p> : null}
          {print === "failed" ? (
            <p role="alert" className="flex items-center gap-2 text-pos-problem">
              <TriangleAlert aria-hidden />
              {t("done.print_failed")}
            </p>
          ) : null}
          {sale.receipt.email ? <p>{t("done.email_queued")}</p> : null}
        </div>
        {sale.taxFree ? <RecordStatus saleId={saleId} initial={sale.taxFree} /> : null}
      </section>
      <aside className="flex flex-col items-center justify-center gap-4">
        {qrValue ? <ReceiptQr value={qrValue} /> : null}
        <button
          type="button"
          onClick={() => void navigate(`/receipts/${saleId}`)}
          className="flex min-h-pos-touch w-full items-center justify-center gap-2 rounded-md border text-lg"
        >
          <ReceiptIcon aria-hidden />
          {t("done.receipt")}
        </button>
        <button
          type="button"
          autoFocus
          onClick={() => void next()}
          className="min-h-24 w-full rounded-lg bg-primary text-2xl font-bold text-primary-foreground"
        >
          {t("done.next")}
        </button>
      </aside>
    </div>
  );
}
